import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uuidV4 from 'uuid/v4';
import Button from '@spothero/ui/button';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import UserUtils from 'user/user-utils';
import AppStoreUtils from 'utils/app-store';
import Icon from 'common/icon/Icon';
import Link from 'common/router/Link';

class NavigationCollapsed extends Component {
    static propTypes = {
        forceAnchors: PropTypes.bool,
        user: PropTypes.object,
        isOpen: PropTypes.bool,
        items: PropTypes.arrayOf(PropTypes.object).isRequired,
        onItemSelected: PropTypes.func,
        onCloseClick: PropTypes.func.isRequired
    };
    state = {
        activeSecondaryMenu: null
    };

    constructor(props) {
        super(props);

        this._IS_IOS = EnvironmentUtils.isiOS();
        this._IS_ANDROID = EnvironmentUtils.isAndroid();
    }

    componentDidMount() {
        DOMUtils.addEventListener({
            event: 'click',
            target: '.NavigationCollapsed-item',
            handler: this._onNavMenuLinkClicked
        });
    }

    componentWillUnmount() {
        DOMUtils.removeEventListener({
            event: 'click',
            target: '.NavigationCollapsed-item',
            handler: this._onNavMenuLinkClicked
        });
    }

    _onNavMenuLinkClicked = evt => {
        const {
            onItemSelected,
            onCloseClick
        } = this.props;

        // if the item clicked has a secondary menu or its a secondary menu title, don't close
        if (DOMUtils.attr(evt.target, 'data-menu') || DOMUtils.hasClass(evt.target, 'NavigationCollapsed-title')) {
            return;
        }

        onCloseClick();

        if (onItemSelected) {
            onItemSelected(evt);
        }
    }

    _onAppStoreLinkClicked = () => {
        const {
            user: {
                email
            }
        } = this.props;
        const args = {
            device: 'ios',
            channel: 'prepurchase'
        };

        if (email) {
            args.email = email;
        }

        AppStoreUtils.appStoreLinkClicked(args);
    };

    _onPlayStoreLinkClicked = () => {
        const {
            user: {
                email
            }
        } = this.props;
        const args = {
            device: 'android',
            channel: 'prepurchase'
        };

        if (email) {
            args.email = email;
        }

        AppStoreUtils.appStoreLinkClicked(args);
    };

    _onToggleSecondaryMenuClicked = evt => {
        evt.persist();
        evt.preventDefault();

        const menuToOpen = DOMUtils.attr(evt.currentTarget, 'data-menu');

        this.setState(prevState => {
            return {
                activeSecondaryMenu: (prevState.activeSecondaryMenu || !menuToOpen)
                    ? null
                    : menuToOpen.replace(' ', '')
            };
        });
    }

    closeSecondaryMenu() {
        this.setState({
            activeSecondaryMenu: null
        });
    }

    render() {
        const {
            forceAnchors,
            user: {
                status,
                isAdmin,
                isSeller
            },
            isOpen,
            items
        } = this.props;
        const classes = classNames(
            'NavigationCollapsed',
            'NavigationCollapsed-primary',
            'scrolling',
            {'NavigationCollapsed-open': isOpen}
        );
        const isLoggedIn = (status === UserUtils.AUTH_STATE.USER);

        return (
            <div
                className={classes}
                role="navigation"
            >
                <div className="NavigationCollapsed-items">
                    {isAdmin &&
                        <Link
                            className="NavigationCollapsed-item NavigationCollapsed-item-admin"
                            to="/admin"
                            forceAnchor
                        >
                            Admin
                        </Link>
                    }
                    {isSeller &&
                        <Link
                            className="NavigationCollapsed-item NavigationCollapsed-item-cp"
                            to="/control-panel"
                            forceAnchor
                        >
                            Control Panel
                        </Link>
                    }
                    {/* This Fragment and temporary key are required for conditional nav links to not be persisted from prerender */}
                    <Fragment
                        key={uuidV4()}
                    >
                        {(isLoggedIn)
                            ? (
                                <Link
                                    className="NavigationCollapsed-item NavigationCollapsed-item-alternate"
                                    to="/account-settings"
                                    forceAnchor
                                >
                                    <Icon name="user-inset" /> My Account
                                </Link>
                            ) : (
                                <Link
                                    className="NavigationCollapsed-item NavigationCollapsed-item-alternate NavigationCollapsed-item-login"
                                    to="/login"
                                    forceAnchor
                                >
                                    <Icon name="user-inset" /> Log In or Sign Up
                                </Link>
                            )
                        }
                    </Fragment>
                    {items.map((item, i) => {
                        const navItemClasses = classNames(
                            'NavigationCollapsed-item',
                            item.classes,
                            {'NavigationCollapsed-item-alternate': item.alternate}
                        );
                        const Tag = (item.dropdown)
                            ? 'a'
                            : Link;
                        const additionalProps = (item.dropdown)
                            ? {
                                href: '#',
                                'data-menu': item.title.replace(' ', ''),
                                onClick: this._onToggleSecondaryMenuClicked
                            }
                            : {
                                to: item.url,
                                forceAnchor: forceAnchors
                            };

                        return (
                            <Tag
                                key={i}
                                className={navItemClasses}
                                {...additionalProps}
                            >
                                {item.icon && <Icon name={item.icon} />} {item.title}
                                {item.dropdown &&
                                    <Icon
                                        className="NavigationCollapsed-item-action"
                                        name="chevron-right"
                                    />
                                }
                            </Tag>
                        );
                    })}
                    {(this._IS_IOS || this._IS_ANDROID) &&
                        <Button
                            className="NavigationCollapsed-item"
                            onClick={(this._IS_IOS) ? this._onAppStoreLinkClicked : this._onPlayStoreLinkClicked}
                        >
                            <Icon name="mobile" /> Download the App
                        </Button>
                    }
                    {isLoggedIn &&
                        <Link
                            className="NavigationCollapsed-item"
                            to="/logout"
                            forceAnchor
                        >
                            Log Out
                        </Link>
                    }
                </div>
                {items.map((item, i) => {
                    if (!item.dropdown) {
                        return null;
                    }

                    const secondaryMenuClasses = classNames(
                        'NavigationCollapsed',
                        'NavigationCollapsed-secondary',
                        'scrolling',
                        {'NavigationCollapsed-open': this.state.activeSecondaryMenu === item.title.replace(' ', '')}
                    );

                    return (
                        <div
                            key={`NavigationCollapsed-secondary-${i}`}
                            className={secondaryMenuClasses}
                        >
                            <div className="NavigationCollapsed-items">
                                <span
                                    className="NavigationCollapsed-item NavigationCollapsed-title"
                                    onClick={this._onToggleSecondaryMenuClicked}
                                >
                                    <Icon
                                        name="chevron-left"
                                    />
                                    {item.title}
                                </span>
                                {item.dropdown.map((ddItem, j) => {
                                    const ddNavItemClasses = classNames(
                                        'NavigationCollapsed-item',
                                        ddItem.classes,
                                        {'NavigationCollapsed-item-alternate': ddItem.alternate}
                                    );

                                    return (
                                        <Link
                                            key={`NavigationCollapsed-item-secondary-${j}`}
                                            className={ddNavItemClasses}
                                            to={ddItem.url}
                                            forceAnchor={forceAnchors}
                                        >
                                            {ddItem.icon && <Icon name={ddItem.icon} />} {ddItem.title}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }
}

export default NavigationCollapsed;
